import React from "react";
import Select from "react-select";
import EditableDataTable from "@components/EditableDataTable";
import { useV2UpdateProjectServiceMutation } from "@generated";
import {
  ProjectService as Service,
  ProjectPhase as Phase,
} from "../../../types";

interface Props {
  orphanedServices: Service[];
  phases: Phase[];
  services: Service[];
  setServices: (services: Service[]) => void;
}

export const UnassignedServicesSection: React.FC<Props> = ({
  orphanedServices,
  phases,
  services,
  setServices,
}) => {
  const [updateService] = useV2UpdateProjectServiceMutation();

  const selectedIds = orphanedServices
    .filter((s) => s.checked)
    .map((s) => s.id);

  const handleSetSelectedIds = (newIds: string[]) => {
    setServices(
      services.map((s) =>
        orphanedServices.some((o) => o.id === s.id)
          ? { ...s, checked: newIds.includes(s.id) }
          : s
      )
    );
  };

  const handleAssignPhase = async (service: Service, phaseId: string) => {
    const position =
      services.filter((s) => s.phase === phaseId && !s.isPending).length + 1;
    const data = {
      id: Number(service.id),
      type: "project-services",
      attributes: {
        position,
      },
      relationships: {
        "project-phase": {
          data: { id: phaseId, type: "project-phases" },
        },
      },
    };
    const response = await updateService({
      id: String(service.id),
      //@ts-ignore
      body: { data },
    });
    //@ts-ignore
    if (!response?.error) {
      setServices(
        services.map((s) =>
          s.id === service.id ? { ...s, phase: phaseId } : s
        )
      );
    }
  };

  const subserviceColumns = [
    {
      id: "subservice name",
      header: () => "SUBSERVICE NAME",
      cell: (sub) => sub.name,
    },
  ];

  const serviceColumns = [
    {
      id: "service name",
      header: () => "SERVICE NAME",
      cell: (service) => service.name,
    },
    {
      id: "assign to phase",
      header: () => "ASSIGN TO PHASE",
      cell: (service) => (
        <div style={{ width: "200px" }}>
          <Select
            options={phases.map((phase) => ({
              value: phase.id,
              label: phase.name,
            }))}
            placeholder="Select a phase..."
            onChange={(selected) => {
              if (selected) handleAssignPhase(service, selected.value);
            }}
          />
        </div>
      ),
    },
  ];

  const records = orphanedServices.map((service) => ({
    ...service,
    status: "pending",
    collapsedData: (
      <div>
        <EditableDataTable
          config={{
            classForTable: `subserviceTable subserviceTableForService${service.id}`,
            expandable: false,
            hasSettings: false,
            striped: true,
          }}
          records={
            service.subservices?.length
              ? [...service.subservices].sort(
                  (a, b) => (a.position || 0) - (b.position || 0)
                )
              : [{ id: "noRow", name: "No subservices" }]
          }
          setData={() => null}
          columns={subserviceColumns}
          selectedIds={[]}
          setSelectedIds={() => null}
        />
      </div>
    ),
  }));

  return (
    <div className="unassignedServicesSection">
      <div className="unassignedServicesHeader">
        <div
          style={{
            fontSize: "22px",
            fontWeight: 700,
            color: "#1c2655",
          }}
        >
          Unassigned Services
        </div>
        <p style={{ color: "#1c2655" }}>
          These services are not currently assigned to a project phase. Please
          assign each to a phase.
        </p>
      </div>
      <EditableDataTable
        config={{
          classForTable: "servicesTable",
          multiselect: true,
          expandable: true,
          striped: true,
        }}
        records={records}
        setData={() => null}
        columns={serviceColumns}
        selectedIds={selectedIds}
        setSelectedIds={handleSetSelectedIds}
      />
    </div>
  );
};
